import { Mail, ShieldCheck, User as UserIcon } from 'lucide-react';
import { Badge } from '@reflex/ui/components/ui/badge';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { useUsers } from '@/hooks/auth/useUsers';
import { useAuth } from '@/hooks/auth/useAuth';
import { ResourcePreviewCard } from '@reflex/ui/components/resource-preview/ResourcePreviewCard';
import type { ResourcePreviewProps } from '@reflex/plugin-api';

const KIND_LABEL = 'User';

/**
 * Hover/click preview for a user owner. Reads from the cached
 * org-scoped `/users` list — same source `useResolvedOwner` pulls names
 * from — so opening a popover never costs a fresh request. Rendered
 * inside the `OwnerChip` HoverCard for user-owned rows.
 */
export function UserPreview({ id }: ResourcePreviewProps) {
  const { data: users, isLoading } = useUsers();
  const { user: currentUser } = useAuth();
  const user = users?.find((u) => u.id === id);

  if (!user) {
    return (
      <ResourcePreviewCard
        kindLabel={KIND_LABEL}
        icon={UserIcon}
        name={isLoading ? 'Loading…' : `User ${id.slice(0, 8)}`}
        subtitle={isLoading ? 'loading' : 'not found'}
        id={id}
      />
    );
  }

  const displayName = user.name ?? user.login;
  const isCurrentUser = currentUser?.id === user.id;

  const meta = (
    <>
      {isCurrentUser ? (
        <Badge
          variant="secondary"
          className="text-[10px] py-0 px-1.5 gap-1"
          data-testid="user-preview-you"
        >
          <ShieldCheck className="h-2.5 w-2.5" />
          You
        </Badge>
      ) : null}
      {user.login ? (
        <Badge variant="outline" className="text-[10px] py-0 px-1.5 font-mono">
          @{user.login}
        </Badge>
      ) : null}
    </>
  );

  const description = user.email ? (
    <p className="flex items-center gap-1.5">
      <Mail className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className="truncate">{user.email}</span>
    </p>
  ) : null;

  return (
    <ResourcePreviewCard
      kindLabel={KIND_LABEL}
      icon={UserIcon}
      media={
        <UserAvatar userName={displayName} avatarUrl={user.avatarUrl ?? undefined} size="md" />
      }
      name={displayName}
      meta={meta}
      description={description}
      id={id}
    />
  );
}
